import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { User } from 'src/app/models/User';
import { environment } from 'src/environments/environment';

import { ListsService } from '../../shared/services/lists.service';
import { LeadList } from './../../models/LeadList';
import { ListFilter } from './../../models/ListFilter';
import { ConfirmationService } from './../../shared/components/confirmation-modal/confirmation.service';
import { ToastService } from './../../shared/components/toast/toast.service';
import { PaginatedResult } from './../../shared/model/paginated-result';
import { AuthService } from './../../shared/services/auth.service';
import { IntegrationsService } from './../../shared/services/integrations.service';
import { Campaign } from 'src/app/models/Campaign';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LegacyLeadService } from './../../shared/services/legacy-lead.service';
import { SearchSelectOption } from './../../shared/components/search-select/search-select.component';

@Component({
  selector: 'app-lists',
  templateUrl: './lists.component.html',
  styleUrls: ['./lists.component.scss']
})
export class ListsComponent implements OnInit, OnDestroy {

  get supportLink() {
    return environment.supportLink;
  }

  integrationId = '';
  name = '';
  page = 1;
  user: User;
  pageSize = 15;
  selectedList: LeadList | null = null;
  migrationNotify = false;
  migrationInProgress: boolean;

  @ViewChild('modalExclusao') modal: any;

  onlyDynamicLists = false;
  onlyManualLists = false;

  result: PaginatedResult<LeadList[]>;
  subscriptions: Subscription[] = [];
  private listSub?: Subscription;
  integrationOptions: SearchSelectOption[] = [{ value: '', label: 'Todas' }];

  // Uma flag por requisição (CU-86akkqppg). Antes esta tela chamava
  // `loadingService.start()` na mão porque o interceptor tinha um `includes('lists')`
  // que desligava o overlay aqui; o esqueleto local torna as duas coisas
  // desnecessárias.
  loadingLists = true;
  loadingIntegrations = true;
  deleteListCampaigns: Campaign[] = [];

  // Apresentação (M9): não existia sinal visual de erro na listagem — uma falha
  // só desligava o esqueleto e caía no "Nenhuma lista cadastrada!" (achado A7 do
  // PLANO.md). `loadError` não muda a chamada nem o payload, só o que aparece.
  loadError = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private autService: AuthService,
    private toastService: ToastService,
    private listsService: ListsService,
    private integrationService: IntegrationsService,
    private confirmationService: ConfirmationService,
    private modalService: NgbModal,
    private legacyLeadService: LegacyLeadService
  ) { }

  ngOnInit(): void {
    this.user = this.autService.getUser();

    this.migrationInProgress = this.user.migration_in_progress;

    this.subscriptions.push(
      this.listsService.importFinished.subscribe(() => this.listPaginated())
    );

    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        this.page = +params.page || 1;
        this.name = params.name || '';
        this.integrationId = params.integration_id || '';
        if (params.dynamic_lists !== undefined) {
          this.onlyDynamicLists = JSON.parse(params.dynamic_lists);
          this.onlyManualLists = JSON.parse(params.manual_lists);
        }
        this.listPaginated();
      }),

      this.autService.userChanged.subscribe((user: User) => {
        this.user = user;
        this.migrationInProgress = user.migration_in_progress;
        this.listPaginated();
      }),

      this.integrationService.listAll().subscribe({
        next: integrations => {
          this.integrationOptions = [
            { value: '', label: 'Todas' },
            ...integrations.map(integration => ({
              value: String(integration.id),
              label: `${integration.platform?.name} - ${integration.name}`
            }))
          ];
          this.loadingIntegrations = false;
        },
        error: () => (this.loadingIntegrations = false),
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.listSub?.unsubscribe();
  }

  listPaginated(): void {
    // Uma requisição por vez (regra 7): sem cancelar a anterior, uma resposta
    // atrasada (erro ou sucesso) podia chegar depois da mais recente e
    // sobrescrever `result`/`loadError` com um estado velho (achado do QA do
    // revisor, 14/10 — ex.: trocar o switch e a integração em seguida).
    this.listSub?.unsubscribe();
    this.loadingLists = true;
    this.loadError = false;
    const filter: ListFilter = {
      name: this.name,
      onlyDynamicLists: this.onlyDynamicLists,
      onlyManualLists: this.onlyManualLists,
      integrationId: this.integrationId
    };

    this.listSub = this.listsService.listPaginated(this.page, this.pageSize, filter).subscribe({
        next: result => {
          this.result = result;
          this.listsService.addAllToStorage(this.result.data);
          if (result.last_page < this.page) {
            this.pageChanged(result.last_page);
          }
          this.loadingLists = false;
        },
        // O `loadingService.stop()` antigo só existia no caminho de sucesso:
        // lista que falhava deixava o overlay travado na tela. Apresentação (M9):
        // `loadError` dá um estado visual honesto em vez de cair no vazio.
        error: () => {
          this.loadingLists = false;
          this.loadError = true;
        },
      });
  }

  filterKeyPress(event: KeyboardEvent): void {
    const key = event?.key?.toLocaleUpperCase();
    if (key === 'ENTER' || this.name === '') {
      this.pageChanged(this.page);
    }
  }

  filterByIntegration(): void {
    this.pageChanged(this.page);
  }

  toggleDinamicLists(checked: boolean): void {
    this.onlyDynamicLists = checked;
    this.pageChanged(this.page);
  }

  toggleManualLists(checked: boolean): void {
    this.onlyManualLists = checked;
    this.pageChanged(this.page);
  }

  pageChanged(page: number): void {
    const queryParams = {
      integration_id: null,
      name: null,
      page,
      dynamic_lists: this.onlyDynamicLists,
      manual_lists: this.onlyManualLists,
      per_page: this.pageSize
    };

    if (this.name) {
      queryParams.name = this.name;
    }

    if (this.integrationId) {
      queryParams.integration_id = this.integrationId;
    }

    this.router.navigate(['/lists'], { queryParams });
  }

  // `legacy`/`fully_migrated` vêm do backend; o `app-accordion-card` antigo
  // recebia `[data]="list"` como `any` e lia direto no template. Os dois
  // getters só replicam a mesma leitura — nenhuma regra nova.
  showLegacyButton(list: LeadList): boolean {
    return !this.migrationInProgress && !!list.legacy && !list.fully_migrated;
  }

  showLegacyPendingHint(list: LeadList): boolean {
    return this.migrationInProgress && !!list.legacy;
  }

  confirmDelete(list: LeadList): void {
    if (list.campaigns.length > 0) {
      this.deleteListCampaigns = list.campaigns;
      this.modalService.open(this.modal);
      return;
    }

    this.confirmationService.show(`Deseja realmente excluir a lista ${list.name}?`, () => this.delete(list.id));
  }

  delete(id: string): void {
    this.subscriptions.push(
      this.listsService.delete(id).subscribe(
        () => {
          this.toastService.success('Lista excluída com sucesso!');
          this.listPaginated();
        },
        () => this.toastService.error('Erro ao excluir lista! Tente novamente mais tarde'))
    );
  }

  confirmLegacy(list: LeadList): void {
    this.selectedList = list;
    this.confirmationService
      .show('Tem certeza que deseja solicitar os dados antigos?',
        () => this.onConfirmLegacy(),
        () => this.confirmationService.hide()
      );
  }

  showMigrationNotify(): void{
    this.migrationNotify = true;

    setTimeout(() => {
      this.migrationNotify = false;
    }, 3000);
  }

  onConfirmLegacy(): void {
    if (this.selectedList) {
      const userId = this.user.id;
      const leadListId = this.selectedList.id;

      this.legacyLeadService.legacyLead(userId, leadListId).subscribe(
        (response) => {
          const user = {...this.user};
          user.migration_in_progress = !this.user.migration_in_progress;

          // `setUser` já emite `userChanged` (CU-86akktwth): o `emit` repetido
          // que havia aqui refazia a listagem e recarregava o menu duas vezes
          // por solicitação de dados antigos.
          this.autService.setUser(user);

          this.showMigrationNotify();
          this.migrationInProgress = true;

        },
        (error) => console.error(error)
      );

      this.modalService.dismissAll();
    }
  }
}
